import { type AuthUser } from '@gb-ticket/shared-types';
import { useState } from 'react';

import { cn } from '@/lib/utils';
import { NavAppTile } from '@/platform/components/nav-app-tile';
import { navTileGridClass } from '@/platform/lib/nav-tile-layout';
import {
  menuModules,
  tilesForMenuModule,
  type MenuModuleId,
} from '@/platform/lib/overview-catalog';
import { productNavRegistration, type ProductMenuTile } from '@/platform/lib/product-nav';

/** Desktop (and any width): pick a module, then add or remove its tiles. */
export function OverviewTilePicker({
  user,
  selectedPaths,
  onToggle,
}: {
  user: AuthUser;
  selectedPaths: readonly string[];
  onToggle: (path: string) => void;
}) {
  const modules = menuModules(user).filter((module) => module.id !== 'overview' && !module.soon);
  const [moduleId, setModuleId] = useState<MenuModuleId>(modules[0]?.id ?? '');
  const selected = modules.find((module) => module.id === moduleId) ?? modules[0];

  if (!selected) {
    return null;
  }

  return (
    <section aria-labelledby="overview-add" className="space-y-4 border-t border-border pt-6">
      <div className="space-y-1">
        <h2 id="overview-add" className="text-lg font-semibold">
          Hinzufügen
        </h2>
        <p className="text-sm text-muted-foreground">
          Modul wählen, dann eine Kachel antippen. Eine gewählte Kachel liegt auf der Übersicht.
        </p>
      </div>
      <ul className="flex flex-wrap gap-1.5" aria-label="Module">
        {modules.map((module) => {
          const active = module.id === selected.id;
          return (
            <li key={module.id}>
              <button
                type="button"
                onClick={() => setModuleId(module.id)}
                className={cn(
                  'rounded-full px-3 py-1.5 text-sm font-semibold whitespace-nowrap',
                  active ? 'bg-primary text-primary-foreground' : 'bg-muted text-foreground',
                )}
              >
                {module.label}
              </button>
            </li>
          );
        })}
      </ul>
      <PickerTiles
        key={selected.id}
        moduleId={selected.id}
        user={user}
        selectedPaths={selectedPaths}
        onToggle={onToggle}
      />
    </section>
  );
}

function PickerTiles({
  moduleId,
  user,
  selectedPaths,
  onToggle,
}: {
  moduleId: MenuModuleId;
  user: AuthUser;
  selectedPaths: readonly string[];
  onToggle: (path: string) => void;
}) {
  const hook = productNavRegistration(moduleId as never)?.useExtraTiles;
  if (!hook) {
    return (
      <TileChoices
        tiles={tilesForMenuModule(moduleId, user)}
        selectedPaths={selectedPaths}
        onToggle={onToggle}
      />
    );
  }
  return (
    <PickerTilesWithExtras
      hook={hook}
      moduleId={moduleId}
      user={user}
      selectedPaths={selectedPaths}
      onToggle={onToggle}
    />
  );
}

function PickerTilesWithExtras({
  hook,
  moduleId,
  user,
  selectedPaths,
  onToggle,
}: {
  hook: () => ProductMenuTile[];
  moduleId: MenuModuleId;
  user: AuthUser;
  selectedPaths: readonly string[];
  onToggle: (path: string) => void;
}) {
  const extras = hook();
  return (
    <TileChoices
      tiles={tilesForMenuModule(moduleId, user, extras)}
      selectedPaths={selectedPaths}
      onToggle={onToggle}
    />
  );
}

function TileChoices({
  tiles,
  selectedPaths,
  onToggle,
}: {
  tiles: readonly ProductMenuTile[];
  selectedPaths: readonly string[];
  onToggle: (path: string) => void;
}) {
  if (tiles.length === 0) {
    return <p className="text-sm text-muted-foreground">Dieses Modul hat keine Kacheln.</p>;
  }
  const chosen = new Set(selectedPaths);
  const groups = groupTiles(tiles);
  return (
    <div className="space-y-4">
      {groups.map((group) => (
        <section key={group.label ?? 'default'} className="space-y-2">
          {group.label ? (
            <h3 className="text-xs font-bold tracking-[0.06em] text-muted-foreground uppercase">
              {group.label}
            </h3>
          ) : null}
          <ul className={navTileGridClass}>
            {group.tiles.map((tile) => {
              const on = chosen.has(tile.to);
              return (
                <li key={tile.id} className="min-w-0">
                  <NavAppTile
                    label={tile.label}
                    path={tile.to}
                    icon={tile.icon}
                    active={on}
                    onOpen={() => onToggle(tile.to)}
                  />
                </li>
              );
            })}
          </ul>
        </section>
      ))}
    </div>
  );
}

function groupTiles(
  tiles: readonly ProductMenuTile[],
): { label?: string; tiles: ProductMenuTile[] }[] {
  const groups: { label?: string; tiles: ProductMenuTile[] }[] = [];
  for (const tile of tiles) {
    const last = groups.at(-1);
    if (last && last.label === tile.group) {
      last.tiles.push(tile);
    } else {
      groups.push({ label: tile.group, tiles: [tile] });
    }
  }
  return groups;
}
